import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { type CoverageArtifactRow } from '@/features/codeCoverage/pages/coverageArtifactRows';
import { CoverageMetricTableCell } from '@/features/codeCoverage/pages/CoverageMetricTableCell';
import {
  resolveCoverageMetricThresholdColor,
  type CoverageMetricThresholdKey,
} from '@/features/codeCoverage/pages/coverageMetricThresholdColors';
import { useCoverageFileTableRowActions } from '@/features/codeCoverage/pages/useCoverageFileTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  STATUS_META_TIMESTAMP_CONTENT_SX,
  STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX,
  TableBreakdownStack,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { EventOutlinedIcon, FolderOutlinedIcon } from '@/ui/primitives/icons';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef, GridColumnVisibilityModel, GridSortModel } from '@/ui/primitives';
import type { MetricThreshold, QueryResult, QueryResult as GridQueryResult } from '@flowqa/shared';

type CoverageFilesTableProps = {
  files: CoverageArtifactRow[];
  queryKey: QueryKey;
  projectId?: string | null;
  initialData?: GridQueryResult<CoverageArtifactRow>;
  loading?: boolean;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
  onQueryMeta?: (meta: { total: number; items: CoverageArtifactRow[] } | null) => void;
};

const COVERAGE_ROW_ESTIMATE_HEIGHT = 72;

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${value.toFixed(2)}%`;

const formatCountPair = (
  covered: number | null | undefined,
  total: number | null | undefined,
): string => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return '-';
  }
  return `${String(covered)} / ${String(total)}`;
};

const buildMetricColumn = (
  field: string,
  headerName: string,
  thresholdKey: CoverageMetricThresholdKey,
  resolvePercent: (row: CoverageArtifactRow) => number | null | undefined,
  resolveCovered: (row: CoverageArtifactRow) => number | null | undefined,
  resolveTotal: (row: CoverageArtifactRow) => number | null | undefined,
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold,
): GridColDef<CoverageArtifactRow> => ({
  field,
  headerName,
  flex: 0.55,
  sortable: false,
  filterable: false,
  align: 'left',
  headerAlign: 'left',
  cellClassName: 'table-metric-cell',
  renderCell: (params) => (
    <TableCellContainer gap="none">
      <CoverageMetricTableCell
        primary={formatPercent(resolvePercent(params.row))}
        secondary={formatCountPair(resolveCovered(params.row), resolveTotal(params.row))}
        primaryColor={resolveCoverageMetricThresholdColor(
          resolvePercent(params.row),
          resolveThreshold(thresholdKey),
        )}
      />
    </TableCellContainer>
  ),
});

const buildCoverageFilesColumns = (
  resolveThreshold: (metricKey: CoverageMetricThresholdKey) => MetricThreshold,
  onEditCoverageFile: (row: CoverageArtifactRow) => void,
  onDeleteCoverageRun: (row: CoverageArtifactRow) => void,
  updateRowTagsColorPendingId: string | null,
): Array<GridColDef<CoverageArtifactRow>> => [
  {
    field: 'fileName',
    headerName: 'File',
    flex: 1.1,
    cellClassName: 'table-name-cell',
    renderCell: (params) => (
      <TableCellContainer gap="none">
        <TableNameCell
          primary={{
            label: params.row.fileName,
            sx: { fontFamily: (theme) => theme.typography.mono.fontFamily },
          }}
        />
      </TableCellContainer>
    ),
  },
  buildMetricColumn(
    'linesPct',
    'Line',
    'line',
    (row) => row.linesPct,
    (row) => row.linesCovered,
    (row) => row.linesTotal,
    resolveThreshold,
  ),
  buildMetricColumn(
    'functionsPct',
    'Function',
    'function',
    (row) => row.functionsPct,
    (row) => row.functionsCovered,
    (row) => row.functionsTotal,
    resolveThreshold,
  ),
  buildMetricColumn(
    'branchesPct',
    'Branch',
    'branch',
    (row) => row.branchesPct,
    (row) => row.branchesCovered,
    (row) => row.branchesTotal,
    resolveThreshold,
  ),
  buildMetricColumn(
    'statementsPct',
    'Statement',
    'statement',
    (row) => row.statementsPct,
    (row) => row.statementsCovered,
    (row) => row.statementsTotal,
    resolveThreshold,
  ),
  {
    field: 'details',
    headerName: getDetailLabel('details'),
    flex: 1,
    sortable: false,
    filterable: false,
    cellClassName: 'table-meta-details-cell',
    renderCell: (params) => {
      const uploadedAt = params.row.uploadCapturedAt ?? params.row.uploadCreatedAt;
      const uploadedLabel = formatRelativeTime(uploadedAt);
      const uploadedTitle = formatDate(uploadedAt, { includeTime: true });

      return (
        <TableCellContainer gap="none">
          <TableBreakdownStack>
            <TableBreakdownLine
              rowTitle="Upload"
              icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={params.row.uploadFileName ?? params.row.fileName}
              disableLinks
              contentSx={{ fontFamily: (theme) => theme.typography.mono.fontFamily }}
            />
            <TableBreakdownLine
              rowTitle="Uploaded"
              icon={<EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={uploadedLabel.length > 0 ? uploadedLabel : '-'}
              valueTitle={uploadedTitle}
              disableLinks
              contentSx={STATUS_META_TIMESTAMP_CONTENT_SX}
              valueWrapperSx={STATUS_META_TIMESTAMP_VALUE_WRAPPER_SX}
            />
            <TableBreakdownLine
              rowTitle="Source"
              icon={<FolderOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              content={params.row.sourceKey ?? params.row.uploadedBy}
              disableLinks
            />
          </TableBreakdownStack>
        </TableCellContainer>
      );
    },
  },
  {
    field: 'actions',
    headerName: '',
    flex: 0.1,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <TableActionsCell
        rowName={params.row.fileName}
        editActionLabel={getEntityActionLabel('edit', 'Coverage File')}
        onEdit={() => {
          onEditCoverageFile(params.row);
        }}
        disableEdit={updateRowTagsColorPendingId === params.row.uploadId}
        showEditTagsAction={false}
        deleteActionLabel={getEntityActionLabel('delete', 'Coverage File')}
        onDelete={() => {
          onDeleteCoverageRun(params.row);
        }}
      />
    ),
  },
];

const compareStrings = (
  leftValue: string | null | undefined,
  rightValue: string | null | undefined,
): number => (leftValue ?? '').localeCompare(rightValue ?? '', undefined, { sensitivity: 'base' });

const compareDates = (
  leftValue: Date | string | null | undefined,
  rightValue: Date | string | null | undefined,
): number => new Date(leftValue ?? 0).getTime() - new Date(rightValue ?? 0).getTime();

const compareNumbers = (leftValue: number | null, rightValue: number | null): number =>
  (leftValue ?? Number.NEGATIVE_INFINITY) - (rightValue ?? Number.NEGATIVE_INFINITY);

const sortFiles = (files: CoverageArtifactRow[], input: GridQueryInput): CoverageArtifactRow[] => {
  const firstSort = input.sorts[0] ?? { field: 'createdAt', direction: 'desc' as const };
  const factor = firstSort.direction === 'asc' ? 1 : -1;
  return [...files].sort((left, right) => {
    let result = 0;
    switch (firstSort.field) {
      case 'fileName':
        result = compareStrings(left.fileName, right.fileName);
        break;
      case 'sourceKey':
        result = compareStrings(left.sourceKey, right.sourceKey);
        break;
      case 'linesPct':
        result = compareNumbers(left.linesPct, right.linesPct);
        break;
      case 'functionsPct':
        result = compareNumbers(left.functionsPct, right.functionsPct);
        break;
      case 'branchesPct':
        result = compareNumbers(left.branchesPct, right.branchesPct);
        break;
      case 'statementsPct':
        result = compareNumbers(left.statementsPct, right.statementsPct);
        break;
      case 'createdAt':
      default:
        result = compareDates(
          left.uploadCapturedAt ?? left.uploadCreatedAt,
          right.uploadCapturedAt ?? right.uploadCreatedAt,
        );
        break;
    }
    return result * factor;
  });
};

export const CoverageFilesTable = ({
  files,
  queryKey,
  projectId = null,
  initialData,
  loading = false,
  initialSortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onQueryMeta,
}: CoverageFilesTableProps): ReactElement => {
  const { thresholds } = useProjectMetricThresholds(projectId);
  const [editTarget, setEditTarget] = useState<CoverageArtifactRow | null>(null);
  const resolveThreshold = useCallback(
    (metricKey: CoverageMetricThresholdKey) => thresholds.coverageRuns[metricKey],
    [thresholds.coverageRuns],
  );
  const {
    deleteLoading,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useCoverageFileTableRowActions();
  const columns = useMemo(
    () =>
      buildCoverageFilesColumns(
        resolveThreshold,
        (row) => {
          setEditTarget(row);
        },
        (row) => {
          onDeleteRow({
            fileName: row.fileName,
            projectId: row.projectId,
            uploadId: row.uploadId,
          });
        },
        updateRowTagsColorPendingId,
      ),
    [onDeleteRow, resolveThreshold, updateRowTagsColorPendingId],
  );
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<CoverageArtifactRow>> => {
      const page = Math.max(input.page, 1);
      const pageSize = input.pageSize;
      const startIndex = (page - 1) * pageSize;
      const sortedFiles = sortFiles(files, input);
      return await Promise.resolve({
        items: sortedFiles.slice(startIndex, startIndex + pageSize),
        total: sortedFiles.length,
        page,
        pageSize,
      });
    },
    [files],
  );

  const gridConfig = {
    persistKey: null,
    syncStateToUrl: false,
    fixedFilters: [],
    initialSortModel,
  };

  return (
    <>
      <ServerDataTable<CoverageArtifactRow>
        queryPath="coverage-files/local-table"
        {...gridConfig}
        initialData={initialData}
        queryFn={queryFn}
        queryKey={queryKey}
        externalLoading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        noRowsText={getEmptyStateMessage('entity.noneFound', { entity: 'coverageFile' })}
        onQueryMeta={onQueryMeta}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnSorting
        disableColumnMenu
        interactionMode="static"
        autoHeight
        getRowHeight={() => 'auto'}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="fileName"], & .MuiDataGrid-cell[data-field="linesPct"], & .MuiDataGrid-cell[data-field="functionsPct"], & .MuiDataGrid-cell[data-field="branchesPct"], & .MuiDataGrid-cell[data-field="statementsPct"], & .MuiDataGrid-cell[data-field="details"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
        ]}
        gridProps={{
          getEstimatedRowHeight: () => COVERAGE_ROW_ESTIMATE_HEIGHT,
          onColumnVisibilityModelChange,
        }}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'coverageFile' })}
        description="This coverage file upload and its associated coverage runs and parsed directory and file coverage will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'coverageFile',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Coverage File"
        entityName={editTarget?.fileName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.uploadId ?? null)}
        cascadeCopy="Tags will be applied to the uploaded coverage file and its associated coverage run."
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.uploadId ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({
            row: {
              id: editTarget.uploadId,
              projectId: editTarget.projectId,
              tags: editTarget.tags,
            },
            tags,
            colors,
          });
          setEditTarget(null);
        }}
      />
    </>
  );
};
